import { useCallback, useState, useMemo, useEffect } from "react";
import { useHistory, generatePath } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import {
  Container,
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  makeStyles,
  createStyles,
  Theme,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

/** Import entities */
import { BranchesRes, Prefecture } from "../../entity/entities";

/** Import utils */
import { colorUtil } from "../../util/ColorUtil";
import { dynamicSort } from "../../util/Array-utils";

/** Import reducer */
import { changeCurrentBranch } from "./inputAnswerSlice";

/** Import hooks */
import { useAppDispatch, useAppSelector } from "../../app/hooks";

/** Import components */
import { MyFooter } from "../footer/MyFooter";
import { MyAppBar } from "../appbar/MyAppBar";
import { appPath, paramName } from "../../router/AppRoute";
import DATA_QA from "../../util/DataQaAttr";

/** Import constants */
import { DEFAULT_OPTION_PREFECTURE } from "../../constant/Enum";

interface IDropDownSelect {
  title: string;
  noOptionText: string;
  listOptions: any;
  selectedOption: any;
  isOpen: boolean;
  handleOpen(isOpen: boolean): void;
  handleSelectedOption(option: any): void;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    option: {
      marginBottom: 3,
      '&[data-focus="true"]': {
        backgroundColor: colorUtil.gray15,
        color: theme.palette.primary.contrastText,
      },
    },
  });
});

const DropdownSelect = ({
  title,
  noOptionText,
  listOptions,
  selectedOption,
  isOpen,
  handleOpen,
  handleSelectedOption
}: IDropDownSelect) => {
  const classes = useStyles();
  return (
    <Box mt={2} mx={3} sx={{ position: "relative" }}>
      <Autocomplete
        clearText=""
        noOptionsText={noOptionText}
        classes={{ ...classes }}
        disableClearable={true}
        blurOnSelect={true}
        fullWidth
        options={listOptions}
        value={selectedOption || undefined}
        popupIcon={null}
        open={isOpen}
        onOpen={() => handleOpen(true)}
        onClose={() => handleOpen(false)}
        autoHighlight
        getOptionLabel={(option: BranchesRes) => option?.name}
        renderOption={(option: BranchesRes) => {
          return <Typography component="span">{option?.name}</Typography>;
        }}
        onChange={(e, value) => handleSelectedOption(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={title}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
            }}
          />
        )}
        id={DATA_QA.SELECT.BRANCH_SELECT}
      />
      <Box
        position="absolute"
        height="100%"
        right="9px"
        top="0"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <IconButton
          style={{ width: "28px", height: "28px" }}
          onClick={() => handleOpen(!isOpen)}
        >
          <ArrowDropDownIcon />
        </IconButton>
      </Box>
    </Box>
  )
}

export function InputBranch(props: {}) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch();
  const history = useHistory();

  const initialBranchList = useAppSelector(
    (state): any[] =>
      state.inputAnswer.session.cxHttpRes?.data?.branches || []
  );
  const currentBranch = useAppSelector(
    (state): BranchesRes | null => state.inputAnswer.selectedBranch
  );
  const getProvinceList = useMemo(() => (
    [{ name: t('question.prefecture.all_provinces_option'), code: DEFAULT_OPTION_PREFECTURE },
    ...Array.from(
      initialBranchList.reduce(
        (map, item) => map.set(item?.prefecture_code, { code: item?.prefecture_code, name: item?.prefecture_name }),
        new Map()
      ).values()
    ).sort(dynamicSort("code"))]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [initialBranchList]);

  const [openCityList, setOpenCityList] = useState<boolean>(false);
  const [provinceOption, setProvinceOption] = useState<Prefecture | null>({ name: t('question.prefecture.all_provinces_option'), code: DEFAULT_OPTION_PREFECTURE });
  const [openBranchList, setOpenBranchList] = useState<boolean>(false);
  const [branchList, setBranchList] = useState<BranchesRes[] | null>([]);
  const [branchOption, setBranchOption] = useState<BranchesRes | null>(currentBranch || null);

  const isDisableSubmitButton = useMemo(
    () => !branchList || branchList.length <= 0 || !branchOption,
    [branchList, branchOption]
  );

  useEffect(() => {
    let filteredBranchesList: BranchesRes[] = initialBranchList.map(({ prefecture_name, prefecture_code, display_name, branches_id }) => {
      return {
        id: branches_id,
        name: display_name,
        prefecture: {
          code: prefecture_code,
          name: prefecture_name
        }
      }
    });
    if (provinceOption?.code && provinceOption?.code !== DEFAULT_OPTION_PREFECTURE) {
      filteredBranchesList = filteredBranchesList.filter(item => item?.prefecture?.code === provinceOption?.code);
    }
    setBranchOption(null);
    setBranchList(filteredBranchesList);
    return undefined;
  }, [initialBranchList, provinceOption?.code]);

  const handleSubmit = useCallback(
    (branch: BranchesRes | null) => {
      if (isDisableSubmitButton) {
        return;
      }
      dispatch(changeCurrentBranch({ branch }));
      history.push(
        generatePath(appPath.shopqr.inputAnswer, {
          [paramName.surveyQestionPageIndexStr]: 0,
        })
      );
    },
    [isDisableSubmitButton, dispatch, history]
  );

  return (
    <Container
      maxWidth={false}
      disableGutters={true}
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <MyAppBar />
      <Box position="relative" flexGrow="1">
        <Box mt={4} mx={3}>
          <Typography variant="h5" align="center">
            <Box
              fontWeight="fontWeightBold"
              style={{ wordBreak: "break-word" }}
            >
              {t('question.please_select_the_applicable_branches_for_the_survey')}
            </Box>
          </Typography>
        </Box>

        <DropdownSelect
          title={t('question.prefecture.province_select')}
          noOptionText={t('question.prefecture.no_city_found')}
          listOptions={getProvinceList}
          selectedOption={provinceOption}
          isOpen={openCityList}
          handleOpen={setOpenCityList}
          handleSelectedOption={setProvinceOption}
        />

        <Box mt={6} mx={3}>
          <Typography variant="body2">
            {t('question.can_search_using_keywords_like_the_branch_name')}
          </Typography>
        </Box>

        <DropdownSelect
          key={branchOption ? branchOption.id : 'none'}
          title={t('question.applicable_branches')}
          noOptionText={t('question.no_matching_stores_found')}
          listOptions={branchList}
          selectedOption={branchOption}
          isOpen={openBranchList}
          handleOpen={setOpenBranchList}
          handleSelectedOption={setBranchOption}
        />

        <Box mt={4} mx={3}>
          <Button
            variant="outlined"
            disabled={isDisableSubmitButton}
            fullWidth
            onClick={() => handleSubmit(branchOption)}
            data-qa={DATA_QA.BUTTON.NEXT}
          >
            {t('question.btn.next')}
          </Button>
        </Box>
      </Box>
      <MyFooter />
    </Container>
  );
}
